import React, { useMemo, useRef, useState } from 'react';
import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';

import { buildLeafletLayer, buildLeafletLayerFromCswGeom } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { Cards, LabelChip, MapWrapper } from 'src/components';
import { getColorScheme } from 'src/utils/colorBrewer';
import { getCombinedBounds } from 'src/components/map/mapHelper';
import { formatArea, upperCaseOnlyFirstLetter as upofl } from 'src/utils/formatters';
import { ZoomOutMap } from '@material-ui/icons';


const zonesMapper = {
  naturalMonument: 'Monumento Natural',
  natureSanctuary: 'Santuario de la Naturaleza',
  forestReserves: 'Reservas Forestales',
  marineReserves: 'Reservas Marinas',
  coastalMarineProtectedArea: 'Área Marina Costera Protegida',
  nationalReserves: 'Reservas Nacionales',
  aquifers: 'Acuíferos',
  nationalAssets: 'Bienes Nacionales',
  biosphere: 'Biosfera',
  ramsar: 'Sitios Ramsar',
  zoit: 'Zonas de Interés Turístico',
  prioritySites: 'Sitios Prioritarios',
  urbanWetlands: 'Humedales Urbanos',
  nationalInventoryWetlands: 'Humedales del Inventario Nacional',
  conservationLandscape: 'Paisaje de Conservación',
  privateConservationInitiatives: 'Iniciativas Privadas de Conservación',
  fiscalConservationBuildings: 'Edificios Fiscales de Conservación',
};

const commonColumns = [
  { title: 'Tipo', render: ({ layerId }) => zonesMapper[layerId] },
  { title: 'Nombre', render: ({ name }) => name ? upofl(name) : 'Sin nombre' },
];

const crossedZonesColumns = [
  ...commonColumns,
  { title: 'Area [ha]', render: ({ areaInHa }) => areaInHa ? formatArea(areaInHa) : '-' },
];

const closestZonesColumns = [
  ...commonColumns,
  { title: 'Distancia [km]', render: ({ distanceInKm }) => distanceInKm ? formatArea(distanceInKm) : '-' },
];

const nationalInventoryWetlandsKey = 'nationalInventoryWetlands';

const heightVH = n => document.documentElement.clientHeight * (n / 100);

const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 16,
  doubleClickZoom: false,
  attributionControl: false,
};

const useStyles = makeStyles(theme => ({
  map: {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  tableTitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
  },
  numberTitle: {
    marginLeft: theme.spacing(0.5),
    borderRadius: '8px',
    fontWeight: 500,
  },
  zones: {
    maxHeight: `${interestAreaMapHeight}vh`,
    height: `${interestAreaMapHeight}vh`,
  },
}));

const interestAreaMapHeight = 70;

const ProtectedAndPriorityZones = ({ protectedAndPriorityZones, interestArea = [] }) => {

  const classes = useStyles();
  const [ zoomToLayer, setZoomToLayer ] = useState(null);
  const [ zoneHighlightedId, setZoneHighlightedId ] = useState(null);
  const [ isHoverOrLeaveType, setIsHoverOrLeaveType ] = useState(false);

  const mapRef = useRef();
  const hasInterestArea = interestArea.length > 0;
  const { crossesZones, closestZones } = useMemo(() => protectedAndPriorityZones.reduce(
    (acc, papz, index) => {
      const { bufferInKm, layerId } = papz;
      if (papz.results.length > 0) {
        papz.results.forEach(p => {
          if (layerId !== nationalInventoryWetlandsKey || p.name) {
            acc.crossesZones.push({ ...p, bufferInKm, layerId, id: `${layerId}-${index}` });
          }
        });
      }
      if (papz.closest) {
        acc.closestZones.push({ ...papz.closest, bufferInKm, layerId, id: `${layerId}-${index}` });
      }
      return acc;
    },
    { crossesZones: [], closestZones: [] },
  ), [ protectedAndPriorityZones ]);

  const interestAreaLayers = useMemo(() =>
    interestArea.map(ia => buildLeafletLayer({
      features: ia.geojson,
      id: ia.id,
      name: ia.name,
    }))
  , [ interestArea ]);

  const closestZonesCount = closestZones.length;
  const crossesZonesCount = crossesZones.length;
  const totalCount = closestZonesCount + crossesZonesCount;

  const colors = useMemo(() => getColorScheme(totalCount), [ totalCount ]);
  const zonesForMap = useMemo(() => [ ...crossesZones, ...closestZones ], [ crossesZones, closestZones ]);

  const zonesLayer = useMemo(() =>
    zonesForMap.map((zone, index) =>
      buildLeafletLayerFromCswGeom({
        features: [ zone.cswFeatGeom ],
        id: zone.id,
        name: zone.name,
        style: () => ({
          weight: 2,
          fillOpacity: 0.8,
          color: zoneHighlightedId === zone.id ? 'yellow' : colors[index],
          fillColor: colors[index],
        }),
      }),
    )
  , [ zonesForMap, colors, zoneHighlightedId ]);

  const zoomTo = getCombinedBounds([
    ...interestAreaLayers,
    ...(zoomToLayer ? zonesLayer.filter(zl => zl.options.id === zoomToLayer) : []),
  ]);

  const [ distanceLineLayer, setDistanceLineLayer ] = useState(null);

  const cardActions = itemData => ([
    {
      Icon: ZoomOutMap,
      tooltip: 'Centrar en el mapa',
      onClick: () => {
        setIsHoverOrLeaveType(false);
        setZoomToLayer(itemData.id);
        if (itemData.distanceGeom) {
          const feature = itemData.distanceGeom;
          const distanceLayer = buildLeafletLayerFromCswGeom({
            features: [ feature ],
            id: `distance-${itemData.id}`,
            name: itemData.name,
            style: {
              color: '#fff',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.6,
              dashArray: '7',
            },
          });

          const labelConfig = { center: true, offset: -5, attributes: { class: 'line-font-border-white' } };
          if (feature.geometry.coordinates[0][0] > feature.geometry.coordinates[1][0]) {
            distanceLayer.setText(feature.properties.label, { ...labelConfig, offset: 14, orientation: 'flip' });
          } else {
            distanceLayer.setText(feature.properties.label, labelConfig);
          }
          setDistanceLineLayer(distanceLayer);
        } else {
          setDistanceLineLayer(null);
        }
      },
    },
  ]);

  const onHover = itemData => {
    setIsHoverOrLeaveType(true);
    setZoneHighlightedId(itemData.id);
  };
  const onLeave = () => {
    setIsHoverOrLeaveType(true);
    setZoneHighlightedId(null);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        {hasInterestArea &&
          <MapWrapper
            id="zones-map"
            className={classes.map}
            options={mapOptions}
            height={heightVH(interestAreaMapHeight)}
            layers={[ ...zonesLayer, ...(distanceLineLayer ? [ distanceLineLayer ] : []), ...interestAreaLayers ]}
            bounds={zoomTo}
            mapRef={mapRef}
            fitBoundsOnUpdate={!isHoverOrLeaveType}
          />
        }
      </Grid>
      <Grid item xs={6}>
        <Box className={classes.zones} display="flex" flexDirection="column">
          <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
            <Typography className={classes.tableTitle} variant="body2" gutterBottom component="span">
              Zonas que intersecan con el área de
              interés <LabelChip className={classes.numberTitle} label={crossesZonesCount} color="gray600" />
            </Typography>
            <Cards
              values={crossesZones}
              columns={crossedZonesColumns}
              actions={cardActions}
              onHover={onHover}
              onLeave={onLeave}
              variant="condensed"
            />
          </Box>
          <Box mt={2} flex={1} display="flex" flexDirection="column" minHeight={0}>
            <Typography className={classes.tableTitle} variant="body2" gutterBottom component="span">
              Zonas cercanas al área de
              interés <LabelChip className={classes.numberTitle} label={closestZonesCount} color="gray600" />
            </Typography>
            <Cards
              values={closestZones}
              columns={closestZonesColumns}
              actions={cardActions}
              onHover={onHover}
              onLeave={onLeave}
              variant="condensed"
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

ProtectedAndPriorityZones.propTypes = {
  protectedAndPriorityZones: PropTypes.array,
  interestArea: PropTypes.array,
};


export {
  ProtectedAndPriorityZones,
};
